'use client';

import { useEffect, useMemo, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { AdvertisersSelect } from '~/entities/Advertiser';
import {
  CampaignMetricsTable,
  QueueStatusCards,
  UnregisteredBannersChart,
  useGetCampaignMetrics,
  useGetMonitoringData,
  useGetUnregisteredStatus,
} from '~/entities/Monitoring';
import {
  ContentTitle,
  datePickerSlotProps,
  Pagination,
  StyledContainer,
  StyledTableContainer,
} from '~/shared';
import type { Advertiser } from '~/types';
import type {
  CampaignMetricsFilters,
  MonitoringFilters as Filters,
} from '~/types/monitoring';

export const MonitoramentoView = () => {
  const [filters, setFilters] = useState<Filters>({ chartType: 'MONITORING' });
  const [chartType, setChartType] = useState('MONITORING');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  const [segmentationType, setSegmentationType] = useState('');
  const [metric, setMetric] = useState<'CTR' | 'CPI' | 'CPC'>('CTR');
  const [status, setStatus] = useState('');
  const [campaignType, setCampaignType] = useState('BANNER');
  const [advertiser, setAdvertiser] = useState<Advertiser | null>(null);
  const [advertiserError, setAdvertiserError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const count = 15;

  const isUnregisteredMode = chartType === 'UNREGISTERED';

  const statusMap: Record<string, number> = {
    INACTIVE: 0,
    ACTIVE: 1,
    DRAFT: 2,
    REVIEW: 3,
    ATTENTION: 4,
    PAUSED: 5,
    DONE: 6,
  };

  const campaignTypeMap: Record<string, number> = {
    BANNER: 1,
    SPONSORED_PRODUCTS: 2,
  };

  const segmentationTypeMap: Record<string, number> = {
    departamento: 1,
    estado: 2,
    tipoNegocio: 3,
    cidade: 4,
    cnpj: 5,
  };

  const metricsFilters: CampaignMetricsFilters = {
    page,
    count,
    metric,
    ...(campaignId && { campaignId }),
    ...(status && { status: statusMap[status] }),
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(segmentationType && {
      segmentationType: segmentationTypeMap[segmentationType],
    }),
    ...(advertiser && { advertiserId: Number(advertiser.id) }),
    ...(campaignType && { campaignType: campaignTypeMap[campaignType] }),
  };

  const { data, isLoading, isError } = useGetMonitoringData(filters);
  const {
    data: unregisteredData,
    isLoading: isLoadingUnregistered,
    isError: isErrorUnregistered,
  } = useGetUnregisteredStatus(isUnregisteredMode);
  const {
    data: metricsData,
    isLoading: isLoadingMetrics,
    isError: isErrorMetrics,
  } = useGetCampaignMetrics(metricsFilters, !isUnregisteredMode);

  useEffect(() => {
    if (chartType === 'UNREGISTERED') {
      setShowAdditionalFilters(false);
    }
  }, [chartType]);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    if (value) {
      setFilters(prev => ({
        ...prev,
        [key]: value,
      }));
    } else {
      setFilters(prev => {
        const { [key]: unused, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleChartTypeChange = (value: string) => {
    setChartType(value);
    handleFilterChange('chartType', value);
    setPage(1);
  };

  const handleCampaignIdChange = (value: string) => {
    setCampaignId(value);
    handleFilterChange('campaignId', value);
    setPage(1);
  };

  const isValidDate = (date: Date | null): date is Date => {
    return date instanceof Date && !Number.isNaN(date.getTime());
  };

  const handleStartDateChange = (value: Date | null) => {
    setStartDate(value);
    setPage(1);
    if (isValidDate(value)) {
      const today = new Date();
      setEndDate(today);

      setFilters(prev => ({
        ...prev,
        startDate: value.toISOString(),
        endDate: today.toISOString(),
      }));
    } else {
      setFilters(prev => {
        const { startDate: removedStartDate, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleEndDateChange = (value: Date | null) => {
    setEndDate(value);
    setPage(1);
    if (isValidDate(value)) {
      setFilters(prev => ({
        ...prev,
        endDate: value.toISOString(),
      }));
    } else {
      setFilters(prev => {
        const { endDate: removedEndDate, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSegmentationTypeChange = (value: string) => {
    setSegmentationType(value);
    handleFilterChange('segmentationType', value);
    setPage(1);
  };

  const handleMetricChange = (value: string) => {
    setMetric(value as 'CTR' | 'CPI' | 'CPC');
    handleFilterChange('metric', value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    handleFilterChange('status', value);
    setPage(1);
  };

  const handleCampaignTypeChange = (value: string) => {
    setCampaignType(value);
    handleFilterChange('campaignType', value);
    setPage(1);

    if (value === 'SPONSORED_PRODUCTS' && segmentationType === 'departamento') {
      setSegmentationType('');
      handleFilterChange('segmentationType', '');
    }
  };

  const handleAdvertiserChange = (value: Advertiser | null) => {
    setAdvertiser(value);
    setAdvertiserError(null);
    setPage(1);
    if (value) {
      handleFilterChange('advertiserId', value.id.toString());
    } else {
      handleFilterChange('advertiserId', '');
    }
  };

  const activeFiltersCount = [
    campaignId,
    segmentationType,
    metric,
    status,
    campaignType,
    advertiser,
  ].filter(Boolean).length;

  const totalPagesMetrics = metricsData?.totalPages ?? 1;
  const totalMetrics = metricsData?.total ?? 0;

  const { totalImpressions, totalClicks } = useMemo(() => {
    if (!metricsData?.list || metricsData.list.length === 0) {
      return { totalImpressions: 0, totalClicks: 0 };
    }

    const totals = metricsData.list.reduce(
      (acc, campaign) => {
        const impressions =
          typeof campaign.impressions === 'number'
            ? campaign.impressions
            : Number(String(campaign.impressions).replace(/[.,\s]/g, '') || 0);
        const clicks =
          typeof campaign.clicks === 'number'
            ? campaign.clicks
            : Number(String(campaign.clicks).replace(/[.,\s]/g, '') || 0);

        return {
          totalImpressions: acc.totalImpressions + impressions,
          totalClicks: acc.totalClicks + clicks,
        };
      },
      { totalImpressions: 0, totalClicks: 0 }
    );

    return totals;
  }, [metricsData]);

  const nextPageHandler = () => {
    if (page < totalPagesMetrics) {
      setPage(prev => prev + 1);
    }
  };

  const prevPageHandler = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  };

  return (
    <>
      <ContentTitle
        title="Monitoramento"
        isFetching={isLoading}
        actions={
          <>
            <Button
              onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
              variant="outlined"
              disabled={isUnregisteredMode}
              sx={{
                width: '56px',
                minWidth: '56px',
                height: '56px',
                position: 'relative',
                p: 0,
              }}
            >
              <FilterListIcon />
              {showAdditionalFilters ? (
                <ExpandLessIcon
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    fontSize: 16,
                  }}
                />
              ) : (
                <ExpandMoreIcon
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    fontSize: 16,
                  }}
                />
              )}
              {activeFiltersCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                  }}
                >
                  {activeFiltersCount}
                </Box>
              )}
            </Button>

            <FormControl variant="filled" sx={{ width: '100%' }}>
              <InputLabel>Tipo do Gráfico</InputLabel>
              <Select
                value={chartType}
                onChange={e => handleChartTypeChange(e.target.value)}
              >
                <MenuItem value="UNREGISTERED">
                  Cliques e impressões não registrados
                </MenuItem>
                <MenuItem value="MONITORING">
                  Monitoramento de campanhas
                </MenuItem>
              </Select>
            </FormControl>

            <DatePicker
              sx={{ width: '100%' }}
              label="Início"
              value={startDate}
              onChange={handleStartDateChange}
              disabled={isUnregisteredMode}
              slotProps={datePickerSlotProps('')}
            />

            <DatePicker
              sx={{ width: '100%' }}
              label="Término"
              value={endDate}
              onChange={handleEndDateChange}
              disabled={isUnregisteredMode}
              slotProps={datePickerSlotProps('')}
            />
          </>
        }
      />

      <Collapse in={showAdditionalFilters}>
        <StyledContainer sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(6, 1fr)',
              },
              gap: 2,
            }}
          >
            <TextField
              variant="filled"
              fullWidth
              label="ID da Campanha"
              value={campaignId}
              onChange={e => handleCampaignIdChange(e.target.value)}
              disabled={isUnregisteredMode}
            />

            <FormControl
              variant="filled"
              fullWidth
              disabled={isUnregisteredMode}
            >
              <InputLabel>Tipo de Segmentação</InputLabel>
              <Select
                value={segmentationType}
                onChange={e => handleSegmentationTypeChange(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="tipoNegocio">Tipo de Negócio</MenuItem>
                <MenuItem value="estado">Estado</MenuItem>
                {campaignType !== 'SPONSORED_PRODUCTS' && (
                  <MenuItem value="departamento">Departamento</MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl
              variant="filled"
              fullWidth
              disabled={isUnregisteredMode}
            >
              <InputLabel>Métrica</InputLabel>
              <Select
                value={metric}
                onChange={e => handleMetricChange(e.target.value)}
              >
                <MenuItem value="CTR">CTR</MenuItem>
                <MenuItem value="CPI">CPI</MenuItem>
                <MenuItem value="CPC">CPC</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="filled"
              fullWidth
              disabled={isUnregisteredMode}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={e => handleStatusChange(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="ACTIVE">Ativa</MenuItem>
                <MenuItem value="INACTIVE">Inativa</MenuItem>
                <MenuItem value="DRAFT">Rascunho</MenuItem>
                <MenuItem value="REVIEW">Em Revisão</MenuItem>
                <MenuItem value="ATTENTION">Atenção</MenuItem>
                <MenuItem value="PAUSED">Pausada</MenuItem>
                <MenuItem value="DONE">Concluída</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="filled"
              fullWidth
              disabled={isUnregisteredMode}
            >
              <InputLabel>Tipo de Campanha</InputLabel>
              <Select
                value={campaignType}
                onChange={e => handleCampaignTypeChange(e.target.value)}
              >
                <MenuItem value="BANNER">Banners</MenuItem>
                <MenuItem value="SPONSORED_PRODUCTS">
                  Produtos Patrocinados
                </MenuItem>
              </Select>
            </FormControl>

            <AdvertisersSelect
              id="advertiser-filter"
              value={advertiser}
              advertiserError={advertiserError}
              onChange={handleAdvertiserChange}
              disabled={isUnregisteredMode}
            />
          </Box>
        </StyledContainer>
      </Collapse>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          width: '100%',
        }}
      >
        {isUnregisteredMode && (
          <>
            {isLoadingUnregistered && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 400,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {!isLoadingUnregistered && isErrorUnregistered && (
              <StyledContainer>
                <Typography color="error">
                  Erro ao carregar dados de status de banners não registrados.
                </Typography>
              </StyledContainer>
            )}

            {!isLoadingUnregistered &&
              !isErrorUnregistered &&
              unregisteredData && (
                <StyledContainer>
                  <UnregisteredBannersChart data={unregisteredData} />
                </StyledContainer>
              )}
          </>
        )}

        {!isUnregisteredMode && (
          <>
            {isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 400,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {!isLoading && isError && (
              <StyledContainer>
                <Typography color="error">
                  Erro ao carregar dados de monitoramento.
                </Typography>
              </StyledContainer>
            )}

            {!isLoading && !isError && data && (
              <>
                <QueueStatusCards
                  impressions={totalImpressions}
                  clicks={totalClicks}
                />

                <StyledContainer>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Métricas de Campanhas
                  </Typography>
                  <StyledTableContainer>
                    <CampaignMetricsTable
                      data={metricsData}
                      isLoading={isLoadingMetrics}
                      isError={isErrorMetrics}
                      metricType={metric}
                    />
                  </StyledTableContainer>
                  <Pagination
                    disabled={
                      totalPagesMetrics === 1 ||
                      isLoadingMetrics ||
                      totalMetrics === 0
                    }
                    page={page}
                    count={totalPagesMetrics}
                    nextPage={nextPageHandler}
                    prevPage={prevPageHandler}
                    onChange={(_, newPage) => setPage(newPage)}
                  />
                </StyledContainer>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
